import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { toast } from "sonner";
import { Loader2, DatabaseZap, UploadCloud, Play, CheckCircle2 } from "lucide-react";

const ENTITY_LABEL = { customers: "Anagrafica Clienti", appointments: "Appuntamenti / Opportunità / Attività" };

// Etichette visibili in italiano — NON modificano le chiavi campo (mapping/API/logica invariati).
const CUSTOMER_LABELS = {
  type: "Tipologia cliente", first_name: "Nome", last_name: "Cognome",
  company_name: "Ragione sociale", fiscal_code: "Codice fiscale", vat_number: "Partita IVA",
  address: "Indirizzo", phone_primary: "Telefono", email: "Email", comune: "Comune",
  provincia: "Provincia", notes: "Note", acquisition_date: "Data acquisizione / registrazione",
  office: "Sede / Ufficio",
  pod_luce: "POD luce", potenza_kw: "Potenza kW", pdr: "PDR gas", consumo_kwh: "Consumo kWh",
};
const APPT_LABELS = {
  legacy_external_id: "ID legacy (SC-xxx)", last_name: "Cognome", first_name: "Nome",
  fiscal_code: "Codice fiscale", vat_number: "Partita IVA", phone_primary: "Telefono", email: "Email",
  operator: "Agente assegnato", lead_origin: "Origine lead", type: "Tipologia appuntamento",
  quote_type: "Tipo preventivo", appt_datetime: "Data e ora appuntamento", location: "Luogo",
  status: "Stato appuntamento", outcome: "Esito appuntamento", recall: "Da richiamare",
  quote_sent_date: "Data invio preventivo", call: "Chiamata", notes: "Note",
};
const fieldLabel = (entity, f) => (entity === "customers" ? CUSTOMER_LABELS : APPT_LABELS)[f] || f;

function statusTone(s) {
  return { CREA: "success", AGGIORNA: "info", SALTA: "pending", ERRORE: "danger", CONFLITTO: "warning" }[s] || "pending";
}

export default function MigrationPage() {
  const [entity, setEntity] = useState("customers");
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(null); // {import_id, columns, crm_fields, sample, row_count}
  const [mapping, setMapping] = useState({});
  const [updateMode, setUpdateMode] = useState("update");
  const [dry, setDry] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setUploaded(null); setMapping({}); setDry(null); setCommitted(null); setFile(null); };

  const doUpload = async () => {
    if (!file) { toast.error("Seleziona un file CSV"); return; }
    setBusy(true); setDry(null); setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity", entity);
      const { data } = await api.post("/migration/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploaded(data);
      // auto-mapping per nome colonna simile
      const auto = {};
      data.crm_fields.forEach((f) => {
        const hit = data.columns.find((c) => c.toLowerCase().replace(/[^a-z]/g, "").includes(f.replace(/_/g, "").slice(0, 4)));
        if (hit) auto[f] = hit;
      });
      setMapping(auto);
      toast.success(`Caricate ${data.row_count} righe`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore upload"); } finally { setBusy(false); }
  };

  const doDryRun = async () => {
    setBusy(true); setCommitted(null);
    try {
      const { data } = await api.post("/migration/dry-run", { import_id: uploaded.import_id, entity, mapping, update_mode: updateMode });
      setDry(data);
      toast.success("Simulazione completata (nessuna scrittura)");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore simulazione"); } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!window.confirm("Confermi l'importazione REALE nel database?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/migration/commit", { import_id: uploaded.import_id, entity, mapping, update_mode: updateMode });
      setCommitted(data);
      toast.success(`Import completato: ${data.summary.CREA} creati, ${data.summary.AGGIORNA} aggiornati`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore commit"); } finally { setBusy(false); }
  };

  const summary = committed?.summary || dry?.summary;
  const rows = committed?.rows || dry?.rows || [];

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="migration-page">
      <div>
        <h1 className="ds-page-title flex items-center gap-2"><DatabaseZap className="h-6 w-6 text-brand" /> Migrazione dati</h1>
        <p className="mt-1 text-sm text-slate-500">Import CSV con anteprima, Simulazione, deduplicazione e report. Riservato alla Direzione.</p>
      </div>

      {/* STEP 1 — Upload */}
      <div className="ds-card space-y-3 p-4" data-testid="migration-upload">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="ds-label">Entità</Label>
            <Select value={entity} onValueChange={(v) => { setEntity(v); reset(); }}>
              <SelectTrigger className="w-[280px]" data-testid="migration-entity"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="customers">Anagrafica Clienti</SelectItem>
                <SelectItem value="appointments">Appuntamenti / Opportunità / Attività</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="ds-label">File CSV</Label>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="migration-file" className="block text-sm" />
          </div>
          <Button onClick={doUpload} disabled={busy || !file} className="gap-1.5" data-testid="migration-upload-btn">
            {busy && !uploaded ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Carica
          </Button>
        </div>
        {uploaded && <p className="text-xs text-slate-500">{uploaded.row_count} righe · {uploaded.columns.length} colonne · {ENTITY_LABEL[entity]}</p>}
      </div>

      {/* STEP 2 — Mapping */}
      {uploaded && (
        <div className="ds-card space-y-3 p-4" data-testid="migration-mapping">
          <h3 className="ds-section-title">Associazione colonne</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {uploaded.crm_fields.map((f) => (
              <div key={f} className="flex items-center gap-2">
                <span className="w-36 shrink-0 text-xs font-medium text-slate-600">{fieldLabel(entity, f)}</span>
                <Select value={mapping[f] || "none"} onValueChange={(v) => setMapping((m) => ({ ...m, [f]: v === "none" ? "" : v }))}>
                  <SelectTrigger className="h-8" data-testid={`map-${f}`}><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{uploaded.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {entity === "customers" && (
            <div className="flex items-center gap-3 pt-2">
              <Label className="ds-label">Su duplicato</Label>
              <Select value={updateMode} onValueChange={setUpdateMode}>
                <SelectTrigger className="w-[200px]" data-testid="migration-update-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="update">Aggiorna (riempie vuoti + riallinea Sede)</SelectItem><SelectItem value="skip">Salta</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={doDryRun} disabled={busy} variant="outline" className="gap-1.5" data-testid="migration-dryrun-btn">
            {busy && !dry ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Esegui Simulazione (nessuna scrittura)
          </Button>
        </div>
      )}

      {/* STEP 3 — Report */}
      {summary && (
        <div className="ds-card space-y-3 p-4" data-testid="migration-report">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge type="success">CREA {summary.CREA}</StatusBadge>
            <StatusBadge type="info">AGGIORNA {summary.AGGIORNA}</StatusBadge>
            <StatusBadge type="pending">SALTA {summary.SALTA}</StatusBadge>
            <StatusBadge type="warning">CONFLITTO {summary.CONFLITTO}</StatusBadge>
            <StatusBadge type="danger">ERRORE {summary.ERRORE}</StatusBadge>
            {committed ? <span className="ml-2 inline-flex items-center gap-1 text-sm font-semibold text-success" data-testid="migration-committed"><CheckCircle2 className="h-4 w-4" /> Import eseguito (batch {committed.batch_id.slice(0, 8)})</span>
              : <span className="ml-2 text-sm text-slate-500" data-testid="migration-dry-label">Anteprima Simulazione — nessun dato scritto</span>}
          </div>
          {entity === "customers" && summary.office && (summary.office.moved || summary.office.stay || summary.office.unresolved) ? (
            <div className="rounded-lg border border-border bg-slate-50 p-3 text-xs" data-testid="migration-office-summary">
              <div className="font-semibold text-slate-700">Riallineamento Sede</div>
              <div className="mt-1 text-slate-600">
                Spostati: <b>{summary.office.moved}</b> · Già corretti: <b>{summary.office.stay}</b> · Sede CSV non riconosciuta (invariati): <b>{summary.office.unresolved}</b>
              </div>
              {Object.entries(summary.office.moves_detail || {}).map(([k, v]) => (
                <div key={k} className="mt-0.5 text-brand">{k}: <b>{v}</b></div>
              ))}
              {Object.keys(summary.office.final || {}).length ? (
                <div className="mt-1 text-slate-500">Distribuzione finale per sede (clienti con Sede riconosciuta): {Object.entries(summary.office.final).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
              ) : null}
            </div>
          ) : null}
          <div className="max-h-[420px] overflow-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-left text-slate-500">
                <tr><th className="px-2 py-2">#</th><th className="px-2 py-2">Nome</th><th className="px-2 py-2">Stato</th><th className="px-2 py-2">Match</th><th className="px-2 py-2">Conflitti / Errori</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.row} data-testid={`migration-row-${r.row}`}>
                    <td className="px-2 py-1.5 text-slate-400">{r.row}</td>
                    <td className="px-2 py-1.5 font-medium text-slate-700">{r.name}</td>
                    <td className="px-2 py-1.5"><StatusBadge type={statusTone(r.status)}>{r.status}</StatusBadge></td>
                    <td className="px-2 py-1.5 text-slate-500">{r.matched_by || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-500">
                      {(r.errors || []).join("; ")}
                      {(r.creates && r.creates.length) ? <div className="text-brand">+ {r.creates.join(", ")}</div> : null}
                      {(r.conflicts || []).map((c, i) => <div key={i} className="text-warning">⚠ {c.field}: CRM "{c.crm}" ≠ CSV "{c.csv}"</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {dry && !committed && (
            <Button onClick={doCommit} disabled={busy} className="gap-1.5" data-testid="migration-commit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Importa definitivamente
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
